import { useEffect, useRef, useState } from "react";
import { Loader, SelectPicker, Tooltip, Whisper } from "rsuite";
import { DataTable, DataTableValue } from "primereact/datatable";
import AllocationSummary from "./allocation-summary";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputNumber } from "primereact/inputnumber";
import Button from "../../../components/button";
import { useAppSelector } from "../../../services/redux/hooks/hooks";
import moment from "moment";
import {
  useAllocationFilters,
  useSmartAllocations,
} from "../../../react-query/hooks/HVolumeAllocation";
import { Column } from "primereact/column";

import InfoOutline from "../../../assets/info-outline.svg";
import ProductInfoIcon from "../../../assets/info-circle-grey.svg";
import { dateFormatterToDDMMMYYYY } from "../../../utils/formatter";
import { sortCommodityFirst, getDefaultCommodity } from "../../../utils/helpers/commodityOrder";
import {
  APPROVAL_STATUS,
  Error_MESSAGE,
  USER_ROLE,
} from "../../../utils/constants";
import { SmartAllocations } from "../../../services/interfaces/IVolumeAllocation";
import CloseIcon from "../../../assets/cross.svg";
// import { Tooltip } from 'primereact/tooltip';
import useResponsiveWidth from "../../../react-query/hooks/useResponsiveWidth";
import {
  convertDateToMonthYear,
  formatDateToLong,
} from "../../../utils/helpers/mapModulesToIcon";
import { queryClient } from "../../../react-query/queryClient";
import { queryKeys } from "../../../react-query/queryKeys";
import SortDefault from "../../../assets/sort_default.svg";
import SortAsc from "../../../assets/sort_asc.svg";
import SortDsc from "../../../assets/sort_desc.svg";
import { SortType } from "rsuite/esm/Table";


const VolumeAllocation = () => {
  const width = useResponsiveWidth();
  const statusClasses = {
    [APPROVAL_STATUS.NoChange]: "border-[#A899F1] bg-[#F7F5FF] text-[#3818D9]",
    [APPROVAL_STATUS.Self]: "border-deloitteGreen bg-[#F0F9F2] text-[#03781D]",
    [APPROVAL_STATUS.Approved]:
      "border-deloitteGreen bg-[#F0F9F2] text-[#03781D]",
    [APPROVAL_STATUS.Pending]: "border-[#E2A1A1] bg-[#F9F2F2] text-[#BA1717]",
    [APPROVAL_STATUS.Requested]: "border-[#79B0E2] bg-[#F0F8FF] text-[#006FD5]",
  };

  const { filters, isLoading } = useAllocationFilters(
    localStorage.getItem("userEmail"),
  );
  const [commodity, setCommodity] = useState<null | string>(null);
  const [trigger, setTrigger] = useState<number>(0);
  const {
    smartAllocations,
    isLoading: smartAllocationsLoading,
    refetch,
  } = useSmartAllocations(
    commodity,
    localStorage.getItem("userEmail"),
    trigger,
  );
  const userDetails = useAppSelector((state) => {
    return state.user;
  });
  const disableScreens = useAppSelector((state) => {
    return state.disableScreens;
  });

  const commodityAccessMap =
    disableScreens?.volumeAllocation &&
      typeof disableScreens.volumeAllocation === "object" &&
      !Array.isArray(disableScreens.volumeAllocation)
      ? (disableScreens.volumeAllocation as Record<string, boolean>)
      : null;

  const allCommodityOptions = filters?.commodities || [];

  const allowedCommodityOptions = commodityAccessMap
    ? (filters?.commodities || []).filter(
      (item: { commodity: string }) => commodityAccessMap[item.commodity],
    )
    : filters?.commodities || [];

  const disabledCommodityValues = commodityAccessMap
    ? allCommodityOptions
      .filter((item: { commodity: string }) => !commodityAccessMap[item.commodity])
      .map((item: { commodity: string }) => item.commodity)
    : [];

  const [districtForAllocation, setDistrictForAllocation] = useState<
    null | DataTableValue | SmartAllocations
  >(null);
  const [showAllocationCriteriaPopUp, setShowAllocationCriteriaPopUp] =
    useState<boolean>(false);
  const [allocationCriteria, setAllocationCriteria] = useState<
    "projected" | "estimated"
  >("projected");
  const [showCustomAllocationView, setShowCustomAllocationView] =
    useState(false);
  const [validation, setValidation] = useState({ isValid: true, message: "" });

  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>([]);

  useEffect(() => {
    if (sortType && smartAllocations?.data) {
      setTableData(
        sortByColumnName(smartAllocations?.data, sortType, sortColumn),
      );
    } else {
      setTableData(smartAllocations?.data); // original order
    }
  }, [smartAllocations?.data]);

  const isChangeFromAllocationSummary = useRef<boolean>(false);

  const fetchAllocationBreakUp = () => {
    /* Custom hook here to fetch all the details and after success, toggle flag fetchAllocationBreakUp */
    if (validation.isValid) {
      setShowAllocationCriteriaPopUp(false);
      setShowCustomAllocationView(true);
    }
  };

  useEffect(() => {
    /* Show popup only when status is no change */
    if (
      districtForAllocation &&
      getApprovalStatus(districtForAllocation?.is_reviewed_flag) ===
      APPROVAL_STATUS.NoChange &&
      !isChangeFromAllocationSummary.current
    ) {
      setShowCustomAllocationView(false);
      setShowAllocationCriteriaPopUp(true);
    } else {
      fetchAllocationBreakUp();
    }
  }, [districtForAllocation]);

  useEffect(() => {
    if (allowedCommodityOptions.length > 0 && commodity === null) {
      setCommodity(getDefaultCommodity(allowedCommodityOptions) ?? null);
      return;
    }

    if (
      commodity !== null &&
      allowedCommodityOptions.length > 0 &&
      !allowedCommodityOptions.some((item: { commodity: string }) => item.commodity === commodity)
    ) {
      setCommodity(getDefaultCommodity(allowedCommodityOptions) ?? null);
      return;
    }

    if (commodity !== null && allowedCommodityOptions.length === 0) {
      setCommodity(null);
    }
  }, [allowedCommodityOptions, commodity]);

  /* Dialog Elements */
  const HeaderElement = (
    <div className="flex flex-row justify-between items-start">
      <div className="flex flex-col justify-start items-start gap-2">
        <span className="font-normal text-fs16 text-[#858585] h-[3vh]">
          Volume Allocations
        </span>
        <span className="font-bold text-fs24">
          What is the total volume you want to allocate in{" "}
          <span className="text-deloitteGreen">
            {districtForAllocation?.district}
          </span>
          <span>?</span>
        </span>
      </div>
      <div>
        <img
          className="cursor-pointer"
          src={CloseIcon}
          onClick={() => {
            setShowAllocationCriteriaPopUp(false);
            setDistrictForAllocation(null);
            setValidation({ isValid: true, message: "" });
          }}
        ></img>
      </div>
    </div>
  );

  const FooterElement = (
    <div className="flex justify-end items-center gap-4">
      <Button
        label="Cancel"
        bgColor="#949494"
        size="md"
        onClick={() => {
          setDistrictForAllocation(null);
          setShowAllocationCriteriaPopUp(false);
          setValidation({ isValid: true, message: "" });
        }}
      ></Button>
      <Button
        label="Continue"
        disabled={!validation.isValid}
        bgColor="#86BC25"
        size="md"
        onClick={() => fetchAllocationBreakUp()}
      ></Button>
    </div>
  );

  /* Dialog Elements end */

  const getApprovalStatus = (statusCode: number) => {
    switch (statusCode) {
      case 0:
        return APPROVAL_STATUS.NoChange;
      case 1:
        return userDetails.role_name === USER_ROLE.Manager
          ? APPROVAL_STATUS.Requested
          : APPROVAL_STATUS.Pending;
      case 2:
        return APPROVAL_STATUS.Self;
      case 3:
        return APPROVAL_STATUS.Approved;
      default:
        throw new Error("Invalid status code");
    }
  };

  const updateEstimatedDemand = (value: number | null) => {
    setDistrictForAllocation({
      ...districtForAllocation,
      estimated_demand: value,
    });
    // let min = Math.min(
    //   Math.ceil(0.7 * districtForAllocation?.projected_releases),
    //   districtForAllocation?.projected_releases - 1
    // );
    // let max = Math.max(
    //   Math.floor(1.3 * districtForAllocation?.projected_releases),
    //   districtForAllocation?.projected_releases + 1
    // );
    // if ((value ?? 0) < min) {
    //   /* Too Low */
    //   setValidation({
    //     isValid: false,
    //     message: `Value should be in ${min}-${max} range. Please enter a higher value.`,
    //   });
    // } else if ((value ?? 0) > max) {
    //   /* Too High */
    //   setValidation({
    //     isValid: false,
    //     message: `Value should be in ${min}-${max} range. Please enter a lower value.`,
    //   });
    // } else {
    //   setValidation({ isValid: true, message: "" });
    // }
    setValidation({ isValid: true, message: "" });
  };

  const handleCallback = (isSuccess: boolean) => {
    if (isSuccess) {
      // setCommodity(JSON.parse(JSON.stringify(commodity)));
      setTrigger(trigger + 1);
      setDistrictForAllocation(null);
    }
  };

  const handleRefresh = () => {
    refetch();
    //  let prevDistrict = districtForAllocation;
    //  setDistrictForAllocation(null);
    //  setTimeout(()=>{
    //  setDistrictForAllocation(prevDistrict);
    //  },0)
  };

  const handleDemandUpdate = (demand: number | null) => {
    let data: SmartAllocations = JSON.parse(
      JSON.stringify(districtForAllocation),
    );
    isChangeFromAllocationSummary.current = true;
    if (demand) {
      data.estimated_demand = demand;
      setDistrictForAllocation(data);
      setAllocationCriteria("estimated");
    } else {
      data.estimated_demand = null;
      setDistrictForAllocation(data);
      setAllocationCriteria("projected");
    }
  };

  const sortByColumnName = (
    data: any[],
    order: "asc" | "desc" | null,
    column: string,
  ) => {
    if (!order) return data;
    return [...data].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (columnName: string, sortType: any) => {
    const order: any = sortType ? (sortType === "asc" ? "desc" : null) : "asc";
    setSortColumn(columnName);
    setSortType(order);

    if (order) {
      setTableData(sortByColumnName(smartAllocations?.data, order, columnName));
    } else {
      setTableData(smartAllocations?.data); // original order
    }
  };

  const renderSortIcon = (field: any) => {
    const iconSize = 12;
    if (sortColumn === field && sortType === "asc") {
      return <img width={iconSize} height={iconSize} src={SortAsc} alt="asc" />;
    } else if (sortColumn === field && sortType === "desc") {
      return (
        <img width={iconSize} height={iconSize} src={SortDsc} alt="desc" />
      );
    } else {
      return (
        <img
          width={iconSize}
          height={iconSize}
          src={SortDefault}
          alt="default"
        />
      );
    }
  };

  return (
    <>
      {isLoading || smartAllocationsLoading ? (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.6, zIndex: 1105 }}
        />
      ) : (
        <div className="flex flex-col justify-stretch items-stretch h-full volume-allocation overflow-hidden">
          <div className="grow shrink-0 basis-[13%] ps-[3%] pr-[5%] pt-[1%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
            <div className="flex justify-between items-center w-full">
              <div className="font-normal text-[2vw]">Volume Allocation</div>
              <div className="flex justify-start items-center gap-8">
                <div className="flex flex-col justify-start items-start">
                  <label className="font-bold text-[0.8vw]">Commodity</label>
                  <SelectPicker
                    style={{ width: width }}
                    data={sortCommodityFirst(allCommodityOptions)}
                    value={commodity ?? undefined}
                    labelKey="commodity"
                    valueKey="commodity"
                    disabledItemValues={disabledCommodityValues}
                    onChange={(value: string | null) => {
                      if (value && !disabledCommodityValues.includes(value)) {
                        setCommodity(value);
                      }
                      setAllocationCriteria("projected");
                      setDistrictForAllocation(null);
                    }}
                    placeholder="Select commodity"
                    cleanable={false}
                    disabled={allCommodityOptions.length === 0}
                  ></SelectPicker>
                </div>
              </div>
            </div>
            <span className="font-bold text-fs16 text-[#858585]">
              {commodity === "Copper Wire"
                ? "Negotiation Month: "
                : "Negotiation Month: "}
              <span className="font-bold underline text-deloitteGreen">
                {commodity === "Copper Wire"
                  ? filters?.date?.[0]?.month_year
                    ? convertDateToMonthYear(filters.date[0].month_year)
                    : ""
                  : filters?.date?.[0]?.month_year
                    ? convertDateToMonthYear(filters.date[0].month_year)
                    : ""}
              </span>
            </span>
          </div>

          <div className="bg-[#F1F1F1] grow shrink pl-[2%] py-[1%] pr-[5%] flex flex-row flex-nowrap justify-between items-start gap-6 h-full w-full">
            {allowedCommodityOptions.length === 0 ? (
              <div className="w-full h-full flex justify-center items-center bg-white shadow-module-card border border-[#bebebe] rounded-md">
                <span className="font-semibold text-fs16 text-[#858585]">
                  You do not have access to Volume Allocation for any commodity.
                </span>
              </div>
            ) : (
              <>
            <div className="flex flex-col gap-4 justify-start items-start basis-[47%] shrink-0 h-full">
              <span className="w-full basis-[5%] bg-white font-bold p-pd13 shadow-module-card border border-[#bebebe] text-fs16 rounded-md">
                Smart Allocation Scenarios ({commodity === "Copper Wire" ? "Lbs" : "TLs"})
              </span>
              <div className="w-full volume-allocation-table h-full">
                {smartAllocations && (
                  <DataTable
                    className="max-w-[37vw]"
                    value={tableData}
                    stripedRows
                    scrollable
                    scrollHeight="72.25vh"
                    showGridlines
                    emptyMessage="No data found corresponding to selected commodity."
                    selectionMode={"single"}
                    selection={districtForAllocation}
                    sortOrder={sortType}
                    dataKey={"district"}
                    style={{ width: "100%", overflowX: "auto" }}
                    onSelectionChange={(e) => {
                      if (e?.value) {
                        const row = e.value as SmartAllocations;
                        const selectedRow = { ...row } as SmartAllocations;
                        if (selectedRow.projected_releases == null && smartAllocations?.vendor_list) {
                          selectedRow.projected_releases = smartAllocations.vendor_list.reduce(
                            (sum: number, vendor: string) => sum + (Number(row[vendor]) || 0), 0
                          );
                        }
                        /* To ensure when pop up opens, it should show projected demand selected by default and, if it is in any other state, then on right side view appropriate option is selected */
                        if (selectedRow.estimated_demand) {
                          setAllocationCriteria("estimated");
                        } else {
                          setAllocationCriteria("projected");
                        }
                        isChangeFromAllocationSummary.current = false;
                        setDistrictForAllocation(selectedRow);
                      }
                    }}
                  >
                    <Column
                      className="cursor-pointer"
                      field="district"
                      // header="District"
                      header={() => (
                        <div
                          className="w-full flex justify-between items-center cursor-pointer"
                          onClick={() => handleSort("district", sortType)}
                        >
                          <div>District</div>{" "}
                          <div>{renderSortIcon("district")}</div>
                        </div>
                      )}
                      // sortable
                      headerStyle={{
                        boxShadow: "inset -2px 0px rgb(134,188,37)",
                      }}
                      style={{
                        boxShadow: "inset -2px 0px rgb(134,188,37)",
                      }}
                      frozen
                      body={(rowdata) => {
                        return (
                          <div className="flex justify-start items-center">
                            <span>{rowdata.district}</span>
                          </div>
                        );
                      }}
                    ></Column>
                    {commodity !== 'Copper Wire' ? smartAllocations?.vendor_list.map((vendor: string) => {
                      return (
                        <Column
                          className="cursor-pointer"
                          key={vendor}
                          field={vendor}
                          header={vendor}
                          style={{ minWidth: "2vw" }}
                        />
                      );
                    }) : smartAllocations?.vendor_list.map((vendor: string) => {
                      return (
                        <Column
                          className="cursor-pointer"
                          key={vendor}
                          field={vendor}
                          header={
                            <div className="flex items-center justify-between gap-1 w-[6vw]">
                              <span className="font-semibold">{vendor}</span>
                            </div>
                          }
                          style={{ minWidth: "2vw" }}
                          body={(rowdata) => {
                            const val = rowdata[vendor];
                            return <span>{val != null ? Number(val).toLocaleString("en-US") : "-"}</span>;
                          }}
                        />
                      );
                    })}
                    <Column
                      className="cursor-pointer"
                      field="is_reviewed_flag"
                      header="Approval Status"
                      frozen
                      headerStyle={{
                        boxShadow: "rgb(134, 188, 37) 3px 0px inset",
                      }}
                      style={{
                        minWidth: "10vw",
                        boxShadow: "rgb(134, 188, 37) 3px 0px inset",
                      }}
                      alignFrozen={"right"}
                      body={(rowdata) => {
                        const msg = `Approved on ${dateFormatterToDDMMMYYYY(
                          (rowdata as any)?.approved_at,
                        )} by ${(rowdata as any)?.approved_by}.`;
                        const submittedMsg = `Submitted on ${dateFormatterToDDMMMYYYY(
                          (rowdata as any)?.approved_at,
                        )} by ${rowdata?.requested_by}.`;
                        return (
                          <div className="flex justify-between items-center">
                            <span
                              className={`border rounded-[50px] font-semibold px-[0.4vw] py-[0.3vh] ${statusClasses[
                                getApprovalStatus(rowdata.is_reviewed_flag)
                              ]
                                }`}
                            >
                              {getApprovalStatus(rowdata.is_reviewed_flag)}
                            </span>
                            {rowdata.is_reviewed_flag > 0 &&
                              rowdata.user_comments && (
                                <span
                                  className="cursor-pointer"
                                  onClick={(event) => event.preventDefault()}
                                >
                                  <Whisper
                                    speaker={
                                      <Tooltip placement="auto">
                                        {rowdata.user_comments}
                                      </Tooltip>
                                    }
                                  >
                                    <img src={InfoOutline} alt="info"></img>
                                  </Whisper>
                                </span>
                              )}
                            {getApprovalStatus(rowdata.is_reviewed_flag) ===
                              APPROVAL_STATUS.Approved && (
                                <span
                                  className="cursor-pointer"
                                  onClick={(event) => event.preventDefault()}
                                >
                                  <Whisper
                                    placement="left"
                                    trigger="hover"
                                    preventOverflow={false}
                                    speaker={<Tooltip>{msg}</Tooltip>}
                                  >
                                    <img
                                      src={ProductInfoIcon}
                                      alt="info"
                                      className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                    ></img>
                                  </Whisper>
                                </span>
                              )}
                            {userDetails.role_name === USER_ROLE.Lead &&
                              getApprovalStatus(rowdata.is_reviewed_flag) ===
                                APPROVAL_STATUS.Pending && (
                                <span
                                  className="cursor-pointer"
                                  onClick={(event) => event.preventDefault()}
                                >
                                  <Whisper
                                    placement="left"
                                    trigger="hover"
                                    preventOverflow={false}
                                    speaker={<Tooltip>{submittedMsg}</Tooltip>}
                                  >
                                    <img
                                      src={ProductInfoIcon}
                                      alt="info"
                                      className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                    ></img>
                                  </Whisper>
                                </span>
                              )}
                          </div>
                        );
                      }}
                    ></Column>
                  </DataTable>
                )}
              </div>
            </div>
            <div className="basis-[53%] h-full w-full overflow-auto">
              <div className="bg-white shadow-module-card justify-start items-center w-full h-full">
                {showCustomAllocationView && districtForAllocation ? (
                  <div className="py-[1.5vh] px-[2vw]">
                    <AllocationSummary
                      data={districtForAllocation as SmartAllocations}
                      role={userDetails.role_name}
                      commodity={commodity ? commodity : ""}
                      allocationCriteria={allocationCriteria}
                      userDetails={userDetails}
                      callback={handleCallback}
                      refreshAllocationSummary={handleRefresh}
                      updateDemand={handleDemandUpdate}
                    ></AllocationSummary>
                  </div>
                ) : (
                  <span className="flex h-full justify-center items-center font-normal text-[3vh]">
                    Select a district to view details
                  </span>
                )}
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      )}
      <Dialog
        draggable={false}
        visible={showAllocationCriteriaPopUp}
        modal
        header={HeaderElement}
        footer={FooterElement}
        style={{ width: "40vw" }}
        closable={false}
        onHide={() => {
          setValidation({ isValid: true, message: "" });
          setShowAllocationCriteriaPopUp(false);
        }}
      >
        <div className="flex flex-col justify-start items-start py-[2vh] px-[1vw] gap-4 radio-criteria text-fs16">
          <div className="flex flex-col justify-start items-start">
            <span className="flex justify-start items-start gap-2">
              <RadioButton
                variant="filled"
                inputId="projected"
                className={allocationCriteria === "projected" ? "checked" : ""}
                name="allocationCriteria"
                value={allocationCriteria}
                onChange={(e) => {
                  setAllocationCriteria("projected");
                  setDistrictForAllocation({
                    ...districtForAllocation,
                    estimated_demand: null,
                  });
                }}
                checked={allocationCriteria === "projected"}
              />
              <div className="flex flex-col justify-start items-start gap-2">
                <label htmlFor="projected">Based on projected releases</label>
                <div className="flex justify-start items-stretch gap-4">
                  <span className="font-semibold max-w-[min(60%,290px)]">
                    Projected Releases for{" "}
                    <span className="text-deloitteGreen">{commodity}</span> in{" "}
                    <span className="text-deloitteGreen">
                      {districtForAllocation?.district}
                    </span>
                  </span>
                  <span className="flex basis-[20%] grow-0">
                    <InputNumber
                      maxFractionDigits={0}
                      className="custom-input-number"
                      value={districtForAllocation?.projected_releases}
                      disabled
                      suffix={commodity === "Copper Wire" ? "\u00a0Lbs" : "\u00a0TLs"}
                    ></InputNumber>
                  </span>
                </div>
              </div>
            </span>
          </div>

          <div className="flex flex-col justify-start items-start">
            <span className="flex justify-start items-start gap-2">
              <RadioButton
                variant="filled"
                inputId="estimated"
                className={allocationCriteria === "estimated" ? "checked" : ""}
                name="allocationCriteria"
                value={allocationCriteria}
                onChange={(e) => setAllocationCriteria("estimated")}
                checked={allocationCriteria === "estimated"}
              />
              <div className="flex flex-col justify-start items-start gap-2">
                <label htmlFor="estimated">Based on estimated demand</label>
                <div className="flex justify-start items-stretch gap-4">
                  <span className="font-semibold max-w-[min(60%,290px)]">
                    Estimated Demand for{" "}
                    <span className="text-deloitteGreen">{commodity}</span> in{" "}
                    <span className="text-deloitteGreen">
                      {districtForAllocation?.district}
                    </span>
                  </span>
                  <span className="flex basis-[20%] grow-0">
                    <InputNumber
                      maxFractionDigits={0}
                      className="custom-input-number"
                      disabled={
                        allocationCriteria !== "estimated" ||
                        ((commodity &&
                          userDetails?.userCommArray?.includes(commodity)
                          ? false
                          : true) &&
                          (userDetails?.role_name === "Lead" ? false : true))
                      }
                      onChange={(event) => updateEstimatedDemand(event.value)}
                      value={districtForAllocation?.estimated_demand}
                      suffix={commodity === "Copper Wire" ? "\u00a0Lbs" : "\u00a0TLs"}
                    ></InputNumber>
                  </span>
                </div>
              </div>
            </span>
          </div>
          <div
            className={`flex justify-start items-center gap-2 bg-[#FFE4E8] rounded px-[1vw] py-[0.5vh] self-stretch ${validation.isValid ? "invisible" : "visible"
              }`}
          >
            <img src={InfoOutline}></img>
            <span className="text-fs14 font-semibold">
              {validation.message}
            </span>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default VolumeAllocation;



